import { AxiosInstance } from '../../../utilities/requestClient';
import type {
  MancoAssetClassKey,
  MancoCisPageModel,
  MancoCounterpartyDropdownPayload,
  MancoCounterpartyExposurePageModel,
  MancoEquityPageModel,
  MancoExposurePageModel,
  MancoFixedIncomePageModel,
} from './types';

export const getMancoExposureUrl = (
  positionDate: string,
  raptorPlatformId?: string,
) => {
  const base = `manco_gem/${positionDate}`;
  return raptorPlatformId
    ? `${base}?raptor_platform_id=${raptorPlatformId}`
    : base;
};

/** GET /manco_gem/:date — manco exposure overview (`OverviewPage` envelope). */
export const getMancoExposurePage = async (
  positionDate: string,
  raptorPlatformId?: string,
): Promise<MancoExposurePageModel> => {
  const { data } = await AxiosInstance.get<MancoExposurePageModel>(
    getMancoExposureUrl(positionDate, raptorPlatformId),
  );
  return data;
};

export const getMancoAssetClassUrl = (
  assetClass: MancoAssetClassKey,
  positionDate?: string,
) =>
  positionDate
    ? `manco_level_asset_class_data/${assetClass}/${positionDate}`
    : `manco_level_asset_class_data/${assetClass}`;

/** GET /manco_level_asset_class_data/equity[/:date] (`OverviewPage` envelope). */
export const getMancoEquityPage = async (
  positionDate?: string,
): Promise<MancoEquityPageModel> => {
  const { data } = await AxiosInstance.get<MancoEquityPageModel>(
    getMancoAssetClassUrl('equity', positionDate),
  );
  return data;
};

/** GET /manco_level_asset_class_data/fixed_income[/:date] (`OverviewPage` envelope). */
export const getMancoFixedIncomePage = async (
  positionDate?: string,
): Promise<MancoFixedIncomePageModel> => {
  const { data } = await AxiosInstance.get<MancoFixedIncomePageModel>(
    getMancoAssetClassUrl('fixed_income', positionDate),
  );
  return data;
};

/** GET /manco_level_asset_class_data/cis[/:date] (`OverviewPage` envelope). */
export const getMancoCisPage = async (
  positionDate?: string,
): Promise<MancoCisPageModel> => {
  const { data } = await AxiosInstance.get<MancoCisPageModel>(
    getMancoAssetClassUrl('cis', positionDate),
  );
  return data;
};

/**
 * GET /manco-counterparties — bare id → name map (not a PageModel),
 * e.g. `{ "336834": "Caceis" }`.
 */
export const getMancoCounterpartyDropdown =
  async (): Promise<MancoCounterpartyDropdownPayload> => {
    const { data } = await AxiosInstance.get<MancoCounterpartyDropdownPayload>(
      'manco-counterparties',
    );
    return data;
  };

export const getMancoCounterpartyExposureUrl = (
  counterparty: string,
  positionDate?: string,
) =>
  positionDate
    ? `manco_counterparty_gem_data/counterparty/${counterparty}/${positionDate}`
    : `manco_counterparty_gem_data/counterparty/${counterparty}`;

/**
 * GET /manco_counterparty_gem_data/counterparty/:id[/:date] —
 * manco counterparty exposure (`OverviewPage` envelope).
 */
export const getMancoCounterpartyExposurePage = async (
  counterparty: string,
  positionDate?: string,
): Promise<MancoCounterpartyExposurePageModel> => {
  const { data } = await AxiosInstance.get<MancoCounterpartyExposurePageModel>(
    getMancoCounterpartyExposureUrl(counterparty, positionDate),
  );
  return data;
};
